import type { ListCallsQuery } from '@crm/contracts';

/**
 * The calls branch of the query-key factory.
 *
 * It lives here rather than in `lib/query-keys.ts` only because that file is
 * being edited by several agents at once; the shape matches the rest exactly,
 * so moving it in is a copy-paste:
 *
 *   calls: {
 *     all: ['calls'] as const,
 *     lists: () => [...queryKeys.calls.all, 'list'] as const,
 *     list: (params: ListCallsQuery) => [...queryKeys.calls.lists(), params] as const,
 *     forContact: (contactId: string) => [...queryKeys.calls.all, 'contact', contactId] as const,
 *     forLead: (contactId: string, leadId: string) =>
 *       [...queryKeys.calls.all, 'lead', contactId, leadId] as const,
 *   },
 *
 * Hierarchical for the same reason the others are: logging a call invalidates
 * every call list without throwing away the contact detail the operator is
 * still looking at.
 */
export const callKeys = {
  all: ['calls'] as const,
  lists: () => [...callKeys.all, 'list'] as const,
  list: (params: ListCallsQuery) => [...callKeys.lists(), params] as const,
  forContact: (contactId: string) => [...callKeys.all, 'contact', contactId] as const,
  forLead: (contactId: string, leadId: string) => [...callKeys.all, 'lead', contactId, leadId] as const,
  /** One call, by id — what the post-call dialog follows while it fills in. */
  detail: (id: number) => [...callKeys.all, 'detail', id] as const,
} as const;
